// src/components/discover/DiscoverFiltersSheet.tsx
//
// Bottom sheet for category filters, hide-closed toggle, and sort
// order. Triggered by the Filter chip in DiscoverPage's header. All
// changes apply immediately (no Save button) — the sheet is
// non-modal in spirit, just a focused control surface.

import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Divider,
  Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DISCOVER_CATEGORIES } from '../../utils/discoverCategories';
import {
  DiscoverSortKey,
  useDiscoverFilters,
} from '../../hooks/useDiscoverFilters';
import { useT } from '../../i18n/useT';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SORT_LABELS: Record<DiscoverSortKey, { label: string; help: string }> = {
  distance: {
    label: 'Closest first',
    help: 'Nearest places at the top of the deck.',
  },
  newest: {
    label: 'Newest first',
    help: 'Most recently added picks first.',
  },
  rating: {
    label: 'Highest rated',
    help: 'Best Google reviews first. Unrated places sink.',
  },
};

export const DiscoverFiltersSheet: React.FC<Props> = ({ open, onClose }) => {
  const t = useT();
  const {
    filters,
    toggleCategory,
    setAllCategories,
    setHideClosed,
    setSortBy,
    resetToDefaults,
    isFilterActive,
  } = useDiscoverFilters();

  const allOn = DISCOVER_CATEGORIES.every((c) =>
    filters.enabledCategories.has(c.key)
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85vh',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            
            {t("Filter & sort")}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isFilterActive && (
              <Button
                size="small"
                onClick={resetToDefaults}
                sx={{ textTransform: 'none' }}
              >
                
                {t("Reset")}
              </Button>
            )}
            <IconButton onClick={onClose} size="small" aria-label={t("Close")}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Body — scrollable */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: 2, py: 2 }}>
          {/* Sort */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
              display: 'block',
              mb: 1,
            }}
          >
            
            {t("Sort")}
          </Typography>
          <RadioGroup
            value={filters.sortBy}
            onChange={(_, v) => setSortBy(v as DiscoverSortKey)}
          >
            {(Object.keys(SORT_LABELS) as DiscoverSortKey[]).map((k) => {
              const def = SORT_LABELS[k];
              return (
                <FormControlLabel
                  key={k}
                  value={k}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">{def.label}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {def.help}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 0.5, mr: 0 }}
                />
              );
            })}
          </RadioGroup>

          <Divider sx={{ my: 2 }} />

          {/* Hide closed */}
          <FormControlLabel
            sx={{ display: 'flex', justifyContent: 'space-between', ml: 0, mr: 0, width: '100%' }}
            labelPlacement="start"
            control={
              <Switch
                checked={filters.hideClosed}
                onChange={(_, c) => setHideClosed(c)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  
                  {t("Hide places that are closed")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  
                  {t("Skips items currently reporting closed on Google. Places with unknown hours stay visible.")}
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 2 }} />

          {/* Categories */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.secondary',
              }}
            >
              
              {t("Categories")}
            </Typography>
            <Button
              size="small"
              onClick={() => setAllCategories(!allOn)}
              sx={{ textTransform: 'none' }}
            >
              {allOn ? 'None' : 'All'}
            </Button>
          </Box>

          <Stack spacing={0}>
            {DISCOVER_CATEGORIES.map((cat) => (
              <FormControlLabel
                key={cat.key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  ml: 0,
                  mr: 0,
                  width: '100%',
                  py: 0.25,
                }}
                labelPlacement="start"
                control={
                  <Switch
                    checked={filters.enabledCategories.has(cat.key)}
                    onChange={() => toggleCategory(cat.key)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {cat.emoji}{' '}{cat.label}
                  </Typography>
                }
              />
            ))}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
